import { ApolloError } from "@apollo/client";
import { ReactNode } from "react";

import NotFoundPage from "components/error/NotFoundPage";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import { VcsIntegration, VcsProvider } from "types/generated";

import { VCSIntegrationsListContextProvider } from "./Context";
import VCSIntegrationsEmpty from "./Empty";
import VCSIntegrationsHasNoResults from "./HasNoResults";
import VCSIntegrationsPageLayout from "./PageLayout";
import VCS_INTEGRATIONS_TABLE_CELLS from "./Table/Cells";
import { SORT_CONFIG, VCS_INTEGRATIONS_TABLE_FILTERS_STORAGE_KEY } from "./Table/constants";
import useVCSIntegrationsTableStore from "./Table/useTableStore";

const VIRTUALIZATION_THRESHOLD = 50;

export type VcsListProps = {
  provider: VcsProvider;
  builtInIntegration?: ReactNode;
  integrations: VcsIntegration[];
  isPageLoading: boolean;
  isPageNotFound: boolean;
  hasNextPage: boolean;
  error: ApolloError | undefined;
  loadMoreItems: () => void;
  refetchIntegrations: () => void;
  hasNoResults: boolean;
};

const VCSIntegrationsList = ({
  provider,
  builtInIntegration,
  integrations,
  isPageLoading,
  isPageNotFound,
  hasNextPage,
  error,
  loadMoreItems,
  refetchIntegrations,
  hasNoResults,
}: VcsListProps) => {
  const trackAnalytics = useAnalytics();

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageNotFound) {
    return <NotFoundPage />;
  }

  return (
    <VCSIntegrationsListContextProvider refetchIntegrations={refetchIntegrations}>
      <VCSIntegrationsPageLayout>
        {builtInIntegration}

        <TableSimple
          data={integrations}
          loading={isPageLoading}
          store={useVCSIntegrationsTableStore}
          ariaLabel="VCS integrations list"
          trackAnalytics={trackAnalytics}
          cellPerColumn={VCS_INTEGRATIONS_TABLE_CELLS}
          filtersStorageKey={VCS_INTEGRATIONS_TABLE_FILTERS_STORAGE_KEY}
          sortConfig={SORT_CONFIG}
          onLoadMore={hasNextPage ? loadMoreItems : undefined}
          virtualize={integrations.length >= VIRTUALIZATION_THRESHOLD}
          emptyState={
            hasNoResults ? (
              <VCSIntegrationsHasNoResults />
            ) : (
              <VCSIntegrationsEmpty provider={provider} />
            )
          }
        />
      </VCSIntegrationsPageLayout>
    </VCSIntegrationsListContextProvider>
  );
};

export default VCSIntegrationsList;
